import React, {useEffect, useRef} from 'react';
import {useTranslation} from 'react-i18next';
import {
  Button,
  ChevronLeftIcon,
  ChevronRightIcon,
  Dialog,
  DisplayMode,
  LoadingOverlay,
  MatchesMediaQuery,
} from '@taopix/taopix-design-system';
import {useTaopix} from '../Context/TaopixContext';
import {useNavigate} from 'react-router-dom';
import useSettings from '../../Hooks/useSettings';
import {useUiState} from '../Context/UserInterface/UserInterfaceContext';
import UserFlowFacade, {UserFlowType} from '../../Facade/UserFlow/UserFlowFacade';
import {useUserFlowState} from '../Context/UserFlow/UserFlowContext';
import {UserFlowStep} from '../../Facade/UserFlow/BaseUserFlow';
import {useAssistant} from '../Context/Assistant/AssistantContext';
import AppAuth from '../Authentication/AppAuth';
import LanguageDialog from '../Language/LanguageDialog';
import NotificationManager from '../Dialogs/Notification/NotificationManager';
import {UserInterfaceActions} from '../Context/UserInterface/UserInterfaceActionTypes';
import {CalendarAssistant} from './Calendars/CalendarAssistant';
import ProjectNameDialog from '../Save/ProjectNameDialog';
import AssistantFlightCheck from './Common/FlightCheck/AssistantFlightCheck';
import {
  AssetMetaInterface,
  CoreProjectService,
  ExperienceType,
  ReducerState,
} from '@taopix/taopix-core/dist/types/types';
import {useSelector} from 'react-redux';
import {FlightCheckModel} from '@taopix/taopix-core/dist/types/Reducers/FlightCheckReducer';
import OptionsAndExtrasWrapper from '../Wrapper/OptionsAndExtrasWrapper';
import {AppBusy, AssistantType, CollectionType, Common, CoreEvent, Lang} from '@taopix/taopix-core';
import {CheckboxAppearanceTypes} from '@taopix/taopix-design-system/dist/types/Components/Checkbox/Checkbox';
import {HeadingLevels} from '@taopix/taopix-design-system/dist/types/Components/Heading/Heading';
import {MediaGalleryProps, SelectionMode} from '../MediaGallery/MediaGallery';
import {PictureSortType} from '../MediaGallery/MediaGalleryOptionsPopout';
import {ThumbnailLayout} from '../MediaGallery/Thumbnails/MediaGalleryThumbnailFactory';
import {StandardAssistant} from './Assistant/StandardAssistant';
import {AdvancedAssistant} from './Assistant/AdvancedAssistant';
import {SmartDesignAssistant} from './SmartDesign/SmartDesignAssistant';
import {PictureSelectionOnlyAssistant} from './Assistant/PictureSelectionOnlyAssistant';
import {ROUTE_ASSISTANT} from '../../Constants';
import {useExperience} from '../Experience/ExperienceProvider';
import {LayoutSelector, UserAssetSelector} from '../../Selectors';
import {CoverMode} from './Step/CreateCoverStep';
import {AppActions} from '../Context/AppActionTypes';
import {useErrorBoundary} from 'react-error-boundary';
import SmallScreenOrientationWarning from '../Mobile/SmallScreenOrientationWarning';
import {AssistantActions} from '../Context/Assistant/AssistantActionTypes';

export type AssistantAssetsProps = {
  assets: AssetMetaInterface[];
  mediaGalleryProps: MediaGalleryProps;
};

export type AssistantNavigationProps = {
  onClose: () => void;
  backButton: React.ReactElement;
  nextButton: React.ReactElement;
  onNext: (...args: any[]) => void;
};

export type AssistantCommonProps = {
  displayMode: DisplayMode;
};

export type AssistantHeaderProps = {
  onContinueInEditor?: (pageNumber?: number) => void;
};

type AssistantProps = {
  open: boolean;
  displayMode: DisplayMode;
};

const Assistant = ({open, displayMode, ...props}: AssistantProps) => {
  // Load language files upfront to prevent the suspense triggering.
  const {t} = useTranslation([
    '*',
    'main',
    'uploadexternalassets',
    'picturemanager',
    'assetservices',
    'helptips',
  ]);
  const taopix = useTaopix();
  const settings = useSettings();
  const ui = useUiState();
  const assistantState = useAssistant();
  const navigate = useNavigate();
  const userFlow = useUserFlowState();
  const server = taopix.core.get('server');
  const isSmallScreen = MatchesMediaQuery('(max-width: 799px)');
  const verySmallScreen = MatchesMediaQuery('(max-width:400px)');
  const flightCheckErrors: FlightCheckModel = useSelector<ReducerState>(
    state => state.flightcheck
  ) as FlightCheckModel;
  const {experience, updateExperience} = useExperience();
  const {showBoundary} = useErrorBoundary();
  const productLayout = useSelector(LayoutSelector);
  const switchInProgress = useRef(false);
  const runCount = useRef<number>(0);
  const currentStep = userFlow.getCurrentStep(UserFlowType.Assistant);

  const assets = (useSelector<ReducerState>(UserAssetSelector) as AssetMetaInterface[]).map(
    (asset: AssetMetaInterface) => {
      const newAsset = {...asset};

      if (!newAsset.hasOwnProperty('label')) {
        newAsset.label = [];
      }

      if (
        Array.isArray(newAsset.label) &&
        assistantState.state.coverAssets[CoverMode.Front].includes(asset.id)
      ) {
        newAsset.label.push(`${t('assistants:str_LabelFrontCoverPicture')}`);
      }
      if (
        Array.isArray(newAsset.label) &&
        assistantState.state.coverAssets[CoverMode.Back].includes(asset.id)
      ) {
        newAsset.label.push(`${t('assistants:str_LabelBackCoverPicture')}`);
      }

      return newAsset;
    }
  );

  /**
   * Gets the route from the Userflow step.
   *
   * @returns The route.
   */
  const getRoute = () => {
    return UserFlowStep.None === currentStep
      ? ''
      : currentStep
          .replace(`${UserFlowType.Assistant}.${UserFlowType.Assistant}`.toLowerCase(), '')
          .replace(`${UserFlowType.Assistant}.`.toLowerCase(), '');
  };

  useEffect(() => {
    if (isSmallScreen) {
      ui.dispatch(UserInterfaceActions.setIsSmallScreen(true));
    } else {
      ui.dispatch(UserInterfaceActions.setIsSmallScreen(false));
    }
  }, [isSmallScreen]);

  useEffect(() => {
    if (!taopix.state.loaded) return;
    if (
      ui.state.fromAssistant.fromAssistant &&
      `#/${ROUTE_ASSISTANT}/` !== settings.state.project.stage
    )
      return;

    if (open) {
      if (
        settings.state.project.stage !== `#/${ROUTE_ASSISTANT}/` &&
        DisplayMode.Fullscreen === displayMode
      ) {
        // Stage doesn't match, url was modified.
        onContinueInEditor();
      } else if (!userFlow.isUserFlowActive(UserFlowType.Assistant)) {
        // Assistant userflow is not active.
        if (!switchInProgress.current) {
          userFlow.activateUserFlow(UserFlowType.Assistant);
          onClose();
        }
      } else if (DisplayMode.Fullscreen === displayMode) {
        navigate(`/${ROUTE_ASSISTANT}/#${getRoute()}`);
      } else {
        navigate(`#${getRoute()}`);
      }
    }
  }, [taopix.state.loaded, open]);

  useEffect(() => {
    const tokens: string[] = [];

    tokens.push(
      taopix.core.subscribe(CoreEvent.CORE_BUSY, (msg: string, data: any) => {
        ui.dispatch(UserInterfaceActions.setIsBusy(data));
      })
    );

    return () => {
      tokens.forEach((token, index) => {
        taopix.core.unsubscribe(token);
        delete tokens[index];
      });
    };
  }, []);

  useEffect(() => {
    if (0 === runCount.current) return;

    // Increment run count in the state when the useRef value updates.
    assistantState.dispatch(AssistantActions.incrementRunCount());
  }, [runCount.current]);

  /**
   * Action to execute after the run method has been called.
   */
  const onAfterRun = (): void => {
    // Increment the run count.
    // Need to track this locally, the In Editor Smart Design Assistant didn't update in time.
    runCount.current++;
  };

  /**
   * Loads the required data and switch to the Editor without reloading the page.
   */
  const onContinueInEditor = (pageNumber?: number): void => {
    /**
     * Actions to execute after a successful switch.
     */
    const onAfterSwitch = (): void => {
      // return if userflow is not active
      // Prevents issues when the component is mounted again during a switch to the editor.
      if (!userFlow.isUserFlowActive(UserFlowType.Assistant)) return;

      ui.dispatch(
        UserInterfaceActions.setFromAssistant({
          fromAssistant: true,
          assistantRan: runCount.current > 0,
        })
      );

      userFlow.deactivateUserFlow(UserFlowType.Assistant);
      if (displayMode === DisplayMode.Fullscreen) {
        userFlow.setProductLayout(taopix.core.get('stateManager').getLayout());
      }
      if (
        Object.keys(flightCheckErrors.criticalErrors).length > 0 ||
        Object.keys(flightCheckErrors.minorErrors).length > 0
      ) {
        userFlow.setCurrentStep(`${UserFlowStep.FlightCheck}.${UserFlowStep.FlightCheckIssueFix}`);
      }

      if (pageNumber) {
        taopix.core.get('stateManager').setSelectedPageNumber(pageNumber, true);
      }

      updateExperience(taopix.core.get('experience') as unknown as ExperienceType);
      switchInProgress.current = false;
      ui.dispatch(UserInterfaceActions.setIsBusy(false));
      taopix.dispatch(AppActions.setIsLoaded(true));
    };

    /**
     * Triggers the switch process.
     *
     * @returns A promise.
     */
    const onSwitch = (): Promise<void> => {
      switchInProgress.current = true;

      return taopix.core
        .get('loader')
        .switch('editor', settings.state.tokens.session)
        .then(response => {
          onAfterSwitch();
        })
        .catch(error => {
          ui.dispatch(UserInterfaceActions.setIsBusy(false));
          taopix.dispatch(AppActions.setIsLoaded(true));
          showBoundary(error);
        });
    };

    ui.dispatch(UserInterfaceActions.setIsBusy(true));

    if (`#/${ROUTE_ASSISTANT}/` === settings.state.project.stage) {
      // Call assistantcomplete to change the stage flag.
      taopix.core
        .get('stateManager')
        .call('assistantcomplete', {blocking: AppBusy.NoCancel})
        .then(() => {
          // Flag the UI to not be loaded to prevent components mounting too soon
          // as we need to load the full data set first.
          taopix.dispatch(AppActions.setIsLoaded(false));

          onSwitch().then(() => {
            navigate('/');
          });
        })
        .catch(error => {
          taopix.dispatch(AppActions.setIsLoaded(true));
          showBoundary(error);
        })
        .finally(() => {
          ui.dispatch(UserInterfaceActions.setIsBusy(false));
        });
    } else {
      onSwitch()
        .then(() => {
          navigate('/');
        })
        .finally(() => {
          ui.dispatch(UserInterfaceActions.setIsBusy(false));
        });
    }
  };

  /**
   * Actions to execute when the assistant is closed.
   */
  const onClose = (): void => {
    if (DisplayMode.Fullscreen === displayMode) {
      console.log('on close');
      onContinueInEditor();
    } else {
      navigate('/');
    }

    userFlow.deactivateUserFlow(UserFlowType.Assistant);
  };

  if (!taopix.state.loaded) {
    return <LoadingOverlay id={'app-loading-overlay'} open={true} overlayColour={'transparent'} />;
  }

  /**
   * Create and returns the back button. This can get cloned to modify its props later if needed.
   *
   * @returns The back button.
   */
  const getBackButton = (): React.ReactElement => {
    const index = Object.values(userFlow.getSteps(UserFlowType.Assistant)).findIndex(step => {
      return step === currentStep;
    });
    const disabled = 0 === index;

    return (
      <Button
        buttonStyle="secondary"
        label={t('str_LabelBack')}
        onClick={() => {
          if (disabled) return;
          userFlow.setPreviousStep();
        }}
        startIcon={<ChevronLeftIcon />}
        hideLabel={ui.state.isSmallScreen && experience.WIZARD.navigation.breadcrumbTrail}
        disabled={disabled}
      />
    );
  };

  /**
   * Create and returns the next button. This can get cloned to modify its props later if needed.
   *
   * @returns The next button.
   */
  const getNextButton = (): React.ReactElement => {
    return (
      <Button
        buttonStyle="primary"
        label={t('str_TitleNext')}
        onClick={e => {
          const nextStep = userFlow.getNextStep(UserFlowType.Assistant);
          if (nextStep === `${UserFlowType.Assistant}.${UserFlowStep.None}`) {
            onContinueInEditor();
          } else {
            userFlow.setNextStep();
          }
        }}
        endIcon={<ChevronRightIcon />}
        hideLabel={ui.state.isSmallScreen && experience.WIZARD.navigation.breadcrumbTrail}
      />
    );
  };

  const mediaGalleryProps: MediaGalleryProps = {
    showUsage: false,
    showLabel: true,
    onItemDragStart: () => {},
    onItemDragEnd: () => {},
    onItemTouchStart: () => {},
    onItemTouchEnd: () => {},
    onDeleteAsset: () => {},
    onRotateAsset: () => {},
    onSelectPicture: (checked, asset) => {},
    hideUsed: false,
    pictureUsage: [],
    thumbnailLayout: ((): ThumbnailLayout => {
      switch (experience.WIZARD.addPicturesStep.thumbnailLayout) {
        case 'fft':
          return 'full-frame-tiles';
        default:
        case 'st':
          return 'square-tiles';
        case 'fil':
          return 'details-list';
      }
    })(),
    sortBy: ((): PictureSortType => {
      switch (experience.WIZARD.addPicturesStep.defaultSortOrder) {
        default:
        case 'dt':
          return PictureSortType.DateTaken;
        case 's':
          return PictureSortType.Size;
        case 'fn':
          return PictureSortType.Filename;
        case 'o':
          return PictureSortType.Orientation;
      }
    })(),
    sortDirection: 'asc',
    imageList: assets,
    selectableImages: true,
    selectedImages: [],
    selectionAppearance: 'checkbox' as CheckboxAppearanceTypes,
    groupedView: false,
    thumbnailSize: verySmallScreen ? 90 : ui.state.isSmallScreen ? 120 : 160,
    gridGap: verySmallScreen ? 5 : 10,
    headingSize: (ui.state.isSmallScreen ? 5 : 3) as HeadingLevels,
    showHeadings: false,
    localeString: Lang.getCode(),
    isSmallScreen: ui.state.isSmallScreen,
    showPreviewButton: false,
    showDeleteButton: false,
    selectionMode: SelectionMode.Multi,
  };

  const assistantActive = userFlow.isUserFlowActive(UserFlowType.Assistant);
  if (!open || !assistantActive) {
    if (switchInProgress.current) {
      return (
        <LoadingOverlay
          id={'assistant-app-switching-overlay'}
          open={true}
          label={t('*:str_MessagePleaseWait')}
          delay={0}
          key={'assistant-app-switching-overlay'}
        />
      );
    }
    return <></>;
  }

  let component: React.ReactElement = <></>;
  switch (true) {
    case productLayout.type === CollectionType.Calendar: {
      component = (
        <CalendarAssistant
          displayMode={displayMode}
          onClose={onClose}
          backButton={getBackButton()}
          assets={assets}
          mediaGalleryProps={mediaGalleryProps}
          nextButton={getNextButton()}
          onNext={() => {}}
          loader={taopix.core.get('loader')}
          onAfterRun={onAfterRun}
        />
      );
      break;
    }
    case AssistantType.Standard === experience.WIZARD.designAssistantType.designAssistantType: {
      component = (
        <StandardAssistant
          assets={assets}
          mediaGalleryProps={mediaGalleryProps}
          displayMode={displayMode}
          onClose={onClose}
          backButton={getBackButton()}
          nextButton={getNextButton()}
          onNext={() => {}}
          onContinueInEditor={onContinueInEditor}
          onAfterRun={onAfterRun}
        />
      );
      break;
    }
    case AssistantType.Advanced === experience.WIZARD.designAssistantType.designAssistantType: {
      component = (
        <AdvancedAssistant
          assets={assets}
          mediaGalleryProps={mediaGalleryProps}
          displayMode={displayMode}
          onClose={onClose}
          backButton={getBackButton()}
          nextButton={getNextButton()}
          onNext={() => {}}
          onContinueInEditor={onContinueInEditor}
          onAfterRun={onAfterRun}
        />
      );
      break;
    }
    case AssistantType.SmartDesign === experience.WIZARD.designAssistantType.designAssistantType: {
      component = (
        <SmartDesignAssistant
          assets={assets}
          mediaGalleryProps={mediaGalleryProps}
          displayMode={displayMode}
          onClose={onClose}
          backButton={getBackButton()}
          nextButton={getNextButton()}
          onNext={() => {}}
          onContinueInEditor={onContinueInEditor}
          onAfterRun={onAfterRun}
        />
      );
      break;
    }
    case AssistantType.PictureSelectionOnly ===
      experience.WIZARD.designAssistantType.designAssistantType: {
      component = (
        <PictureSelectionOnlyAssistant
          assets={assets}
          mediaGalleryProps={mediaGalleryProps}
          displayMode={displayMode}
          onClose={onClose}
          backButton={getBackButton()}
          nextButton={getNextButton()}
          onNext={() => {}}
          onContinueInEditor={onContinueInEditor}
          onAfterRun={onAfterRun}
        />
      );
      break;
    }
  }

  const showLoading = taopix.state.loaded && ui.state.busy;

  return (
    <>
      <LoadingOverlay
        id={'assistant-app-busy-overlay'}
        open={showLoading}
        label={t('*:str_MessagePleaseWait')}
        delay={500}
        key={'assistant-app-busy-overlay'}
      />
      <Dialog
        id="assistant"
        open={open}
        role="main"
        displayMode={DisplayMode.Fullscreen === displayMode ? 'full-screen' : 'modal'}
        width={DisplayMode.Fullscreen !== displayMode ? 1200 : undefined}
        height={DisplayMode.Fullscreen !== displayMode ? 760 : undefined}
        onClose={onClose}
        showCloseButton={true}
        theme={'tpx-theme-main'}
        themeTip="Base"
        focusFirstElement={false}
        title={t('assistants:str_TitleDesignAssistant', {defaultValue: 'Design Assistant'})}
        showTitle={false}
        isolateKeys={false}
      >
        {component}
      </Dialog>
      {DisplayMode.Fullscreen === displayMode && (
        <>
          <AppAuth server={server} authentication={settings.state?.authentication} />
          <OptionsAndExtrasWrapper server={server} />
          <LanguageDialog isOpen={ui.state.languageDialogOpen} />
          <NotificationManager />
          <ProjectNameDialog
            open={userFlow.isStepActive(UserFlowType.Save, UserFlowStep.ProjectName)}
          />
          <AssistantFlightCheck
            isOpen={userFlow
              .getCurrentStep(UserFlowType.FlightCheck)
              .includes(UserFlowStep.FlightCheckReview)}
            onContinueInEditor={onContinueInEditor}
          />
          <SmallScreenOrientationWarning />
        </>
      )}
    </>
  );
};

export default Assistant;
